import axios from "axios";
import config from "../../config/config";
import * as mocks from "./specialAccessUser.mocks";
import { SpecialAccessUser } from '@/types/SpecialAccessUser';

const API_URL = `${config.apiBaseUrl}/admin/special-access-user`;

const axiosClient = axios.create({
    baseURL: API_URL,
    headers: {
        "Content-Type": "application/json",
    },
    timeout: config.defaultTimeout,
});

axiosClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("token");
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

const apiGetSpecialAccessUserById = async (userId: string): Promise<SpecialAccessUser> => {
    try {
        const response = await axiosClient.get<SpecialAccessUser>(`${API_URL}/${userId}`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.error("Axios error during getSpecialAccessUserById:", error.message);
        } else {
            console.error("Unexpected error during getSpecialAccessUserById:", error);
        }
        throw error;
    }
};

const apiGetSpecialAccessUsers = async (
    filters: { search?: string } = {},
    page: number = 1,
    limit: number = 9
): Promise<{ data: SpecialAccessUser[]; total: number }> => {
    try {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        if (filters.search) {
            params.append('search', filters.search);
        }

        const response = await axiosClient.get<SpecialAccessUser[]>(`${API_URL}?${params.toString()}`);
        // Transformar a resposta da API (array) em { data, total }
        return {
            data: response.data,
            total: response.data.length
        };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.error("Axios error during apiGetSpecialAccessUsers:", error.message);
        } else {
            console.error("Unexpected error during apiGetSpecialAccessUsers:", error);
        }
        throw error;
    }
};

const apiUpdateSpecialAccessUser = async (userId: string, data: Partial<SpecialAccessUser>): Promise<void> => {
    try {
        await axiosClient.put(`${API_URL}/${userId}`, data);
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.error("Axios error during apiUpdateSpecialAccessUser:", error.message);
        } else {
            console.error("Unexpected error during apiUpdateSpecialAccessUser:", error);
        }
        throw error;
    }
};

const apiResetUserPassword = async (userId: string): Promise<void> => {
    try {
        await axiosClient.post(`${API_URL}/${userId}/reset-password`);
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.error("Axios error during apiResetUserPassword:", error.message);
        } else {
            console.error("Unexpected error during apiResetUserPassword:", error);
        }
        throw error;
    }
};

const apiBlockUserAccess = async (userId: string): Promise<void> => {
    try {
        await axiosClient.post(`${API_URL}/${userId}/block-access`);
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.error("Axios error during apiBlockUserAccess:", error.message);
        } else {
            console.error("Unexpected error during apiBlockUserAccess:", error);
        }
        throw error;
    }
};

const apiDeleteSpecialAccessUser = async (userId: string): Promise<void> => {
    try {
        await axiosClient.delete(`${API_URL}/${userId}`);
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.error("Axios error during apiDeleteSpecialAccessUser:", error.message);
        } else {
            console.error("Unexpected error during apiDeleteSpecialAccessUser:", error);
        }
        throw error;
    }
};

export const getSpecialAccessUserById = async (userId: string): Promise<SpecialAccessUser> => {
    return await (config.useRealApi ? apiGetSpecialAccessUserById(userId) : mocks.getSpecialAccessUserById(userId));
};

export const getSpecialAccessUsers = async (filters: { search?: string } = {}, page: number = 1, limit: number = 9): Promise<{ data: SpecialAccessUser[]; total: number }> => {
    return await (config.useRealApi ? apiGetSpecialAccessUsers(filters, page, limit) : mocks.getSpecialAccessUsers(filters, page, limit));
};

export const updateSpecialAccessUser = async (userId: string, data: Partial<SpecialAccessUser>): Promise<void> => {
    return await (config.useRealApi ? apiUpdateSpecialAccessUser(userId, data) : mocks.updateSpecialAccessUser(userId, data)); 
};

export const resetUserPassword = async (userId: string): Promise<void> => {
    return await (config.useRealApi ? apiResetUserPassword(userId) : mocks.resetUserPassword(userId)); 
};

export const blockUserAccess = async (userId: string): Promise<void> => {
    return await (config.useRealApi ? apiBlockUserAccess(userId) : mocks.blockUserAccess(userId)); 
};

export const deleteSpecialAccessUser = async (userId: string): Promise<void> => {
    return await (config.useRealApi ? apiDeleteSpecialAccessUser(userId) : mocks.deleteSpecialAccessUser(userId)); 
};
